import { useMemo, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import { useUrlParams } from '../params'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { Host, InstallInfo, NodeInfo } from '../../api'
import { Button } from '../../components/ui'
import { HostCard, InstallProgress, LatestJob, PendingHosts } from '../Hosts'
import { AddNodeDialog, Modal, NODES_KEY, NodeManage, fetchNodes, mbps, outOfSync, outputsText, useProvisioning, type Dialog } from '../Nodes'
import { FactorBadge, SecondFactorProvider } from '../SecondFactor'
import { HostsLiveContext, hostErrorText } from '../provisioning'
import { nodePath, type RelayRole } from './api'
import { RelayDataBanner } from './banner'
import { bps } from './format'
import { useRelayLive } from './live'
import { LoadCell, RoleGlyphs } from './nodeParts'
import { PROBLEMS, SORTS, filterNodes, withLive, type NodeFilter, type NodeSort, type StateFilter } from './nodes'
import { RelayKeyBlock } from './RelayKey'
import { INSTALLING_SPEC, NODE_SPEC, RELAY_STATE_SPEC, nodeIssues, nodeView, type NodeView } from './states'
import { LogicVersion } from './NodeUpdate'
import { FleetUpdateButton } from './FleetUpdate'
import { GearIcon, Lifecycle, StatusCell } from './StatusCell'
import { Empty, ErrorBox, SkeletonRows, StateBadge } from './ui'
import { useFailover } from './NodeFailover'
import { groupPath, isNodeGroup } from './groups'
import { t } from '../../i18n'
import { Icon, Note, Tag } from '../../ui'
import { CreateGroupButton } from './GroupCreate'
import { DefaultGroupPanel } from './DefaultGroup'
import type { FailoverGroup } from '../../api'

/*
 * Network › Nodes (handoff §0, design #nodes; handoff/nodes.md): the fleet with
 * status (glyph + word, reasons in words, and icons for the host agent, the
 * peers list and the clock), roles as shapes, region and provider, load and
 * streams; filters, sort and
 * search kept in the URL (?state=&region=&role=&q=&sort=). Desktop: a table that
 * fits 1280 px without sideways scroll (fixed layout, cells truncate with a
 * title); phone (< 768 px): cards. The name opens the node card; the gear opens
 * the node's settings under the row: relay key, host, policy, secret, revoke.
 *
 * Add node (M2.4 over SSH, or the manual token) and the hosts without a node
 * are unchanged from the M2.4 page.
 */

export function NetworkNodesPage() {
  return (
    <SecondFactorProvider>
      <NodesPage />
    </SecondFactorProvider>
  )
}

const STATE_CHIPS: { value: StateFilter; label: string; glyph?: string }[] = [
  { value: '', label: 'All' },
  { value: 'problems', label: 'Problems' },
  { value: 'online', label: 'Online', glyph: NODE_SPEC.online.glyph },
  { value: 'degraded', label: 'Degraded', glyph: NODE_SPEC.degraded.glyph },
  { value: 'offline', label: 'No link', glyph: NODE_SPEC.offline.glyph },
  { value: 'not_in_peers', label: 'Not in peers', glyph: NODE_SPEC.not_in_peers.glyph },
]
const ROLES: { value: '' | RelayRole; label: string }[] = [
  { value: '', label: 'All roles' },
  { value: 'ingress', label: 'Ingress' },
  { value: 'egress', label: 'Egress' },
  { value: 'hub', label: 'Hub' },
  { value: 'relay', label: 'Relay' },
]
const SEL = 'min-h-9 rounded-md border border-line-2 bg-bg px-2 text-[13px] text-text-soft focus-visible:border-accent max-md:min-h-[44px]'

function useFilter(): [NodeFilter, (patch: Partial<NodeFilter>) => void] {
  const { params: p, update } = useUrlParams()
  const f: NodeFilter = {
    q: p.get('q') ?? '',
    state: (p.get('state') ?? '') as StateFilter,
    region: p.get('region') ?? '',
    role: (p.get('role') ?? '') as NodeFilter['role'],
    sort: (SORTS.some((s) => s.value === p.get('sort')) ? p.get('sort') : 'state') as NodeSort,
    revoked: p.get('revoked') === '1',
  }
  // the shared helper starts from the URL as it is now: two quick changes must not undo each other
  const set = (patch: Partial<NodeFilter>) =>
    update(Object.fromEntries(Object.entries(patch).map(([k, v]) => [k, k === 'revoked' ? (v ? '1' : '') : k === 'sort' && v === 'state' ? '' : String(v ?? '')])))
  return [f, set]
}

function NodesPage() {
  const qc = useQueryClient()
  const relay = useRelayLive()
  const q = useQuery({ queryKey: NODES_KEY, queryFn: fetchNodes, refetchInterval: 5000 })
  const prov = useProvisioning()
  const [dialog, setDialog] = useState<Dialog>(null)
  const location = useLocation()
  const done = (location.state as { done?: string } | null)?.done
  const [open, setOpen] = useState<string | null>(null)
  const [f, setF] = useFilter()
  // the relay state of a node from the stream (node_relay) over the 5 s list
  const nodes = useMemo(() => (q.data ?? []).map((n) => (n.relay ? withLive(n, relay.nodes.get(n.id)) : n)), [q.data, relay.nodes])
  const showRelay = relay.available !== false && nodes.some((n) => n.relay)
  const showLoad = nodes.some((n) => n.load || n.clock)
  const showRoles = nodes.some((n) => n.roles?.length)
  const hosts = (prov.hosts.data ?? []).filter((h) => !h.revoked_at)
  const info = prov.keys.data?.install
  const byNode = new Map(hosts.filter((h) => h.node_id).map((h) => [h.node_id as string, h]))
  const pending = hosts.filter((h) => !h.node_id || !nodes.some((n) => n.id === h.node_id))
  const installing = pending.filter((h) => h.installing)
  const active = nodes.filter((n) => n.status !== 'revoked')
  const revoked = nodes.length - active.length
  const online = nodes.filter((n) => n.status === 'online').length
  const refresh = () => qc.invalidateQueries({ queryKey: NODES_KEY })
  const regions = useMemo(() => [...new Set(nodes.map((n) => n.region).filter(Boolean))].sort(), [nodes])
  const counts = useMemo(() => {
    const c: Partial<Record<NodeView, number>> = {}
    for (const n of nodes) {
      const v = nodeView(n)
      c[v] = (c[v] ?? 0) + 1
    }
    return c
  }, [nodes])
  const list = useMemo(() => filterNodes(nodes, f), [nodes, f])
  const filtered = !!(f.q || f.state || f.region || f.role)
  const reset = () => setF({ q: '', state: '', region: '', role: '' })
  const toPlatforms = active.reduce((a, n) => a + (n.status === 'online' ? (n.egress_bps ?? 0) : 0), 0)

  const chipCount = (v: StateFilter) => (v === '' ? active.length : v === 'problems' ? PROBLEMS.reduce((a, x) => a + (counts[x] ?? 0), 0) : (counts[v] ?? 0))

  return (
    <HostsLiveContext.Provider value={prov.live}>
      <div className="flex flex-col gap-4" data-testid="network-nodes">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold">Nodes</h1>
          <span className="num text-sm text-muted" data-testid="nodes-online">
            {online} online of {active.length}
          </span>
          <span className="ml-auto flex flex-wrap items-center justify-end gap-3">
            {prov.on && <FactorBadge />}
            {prov.on && hosts.some((h) => h.node_id) && <FleetUpdateButton hosts={hosts} nodes={nodes} info={info} />}
            <CreateGroupButton />
            <Button variant="primary" data-testid="node-add" className="max-md:min-h-[44px]" onClick={() => setDialog({ kind: 'add' })}>
              Add node
            </Button>
          </span>
        </div>
        {done && (
          <Note role="status" data-testid="nodes-done">
            {done}
          </Note>
        )}
        <DefaultGroupPanel />
        {showRelay && <RelayDataBanner live={relay} />}
        {prov.hosts.error && <ErrorBox text={hostErrorText(prov.hosts.error)} onRetry={() => void prov.hosts.refetch()} busy={prov.hosts.isFetching} />}

        {active.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by state">
            {STATE_CHIPS.filter((c) => c.value === '' || c.value === 'problems' || chipCount(c.value) > 0 || f.state === c.value).map((c) => (
              <button
                key={c.value || 'all'}
                type="button"
                aria-pressed={f.state === c.value}
                data-testid={`nodes-chip-${c.value || 'all'}`}
                onClick={() => setF({ state: c.value })}
                className={`num inline-flex min-h-8 items-center gap-1.5 rounded-md border px-3 text-[13px] max-md:min-h-[44px] ${f.state === c.value ? 'border-live/50 bg-live/[.12] text-live' : 'border-line-2 text-text-soft hover:text-text'}`}
              >
                {c.glyph && <span aria-hidden>{c.glyph}</span>}
                {c.label} <span className="font-mono">{chipCount(c.value)}</span>
              </button>
            ))}
            {toPlatforms > 0 && (
              <span className="ml-auto hidden text-xs text-muted md:inline">
                To platforms <b className="num font-mono text-text-soft">{bps(toPlatforms)}</b>
              </span>
            )}
          </div>
        )}

        <section className="rs-corners min-w-0 rounded-lg border border-line" aria-label="Nodes">
          <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
            <label className="flex min-h-9 min-w-0 flex-1 basis-[220px] items-center gap-2 rounded-md border border-line-2 bg-bg px-2.5 focus-within:border-accent focus-within:shadow-focus max-md:min-h-[44px] md:max-w-[320px]">
              <span className="text-muted" aria-hidden>
                ⌕
              </span>
              <input
                value={f.q}
                onChange={(e) => setF({ q: e.target.value })}
                placeholder="Name, id, provider, relay key"
                aria-label="Search nodes"
                data-testid="nodes-search"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted"
              />
            </label>
            <select aria-label="Region" data-testid="nodes-region" className={SEL} value={f.region} onChange={(e) => setF({ region: e.target.value })}>
              <option value="">All regions</option>
              {regions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            {showRoles && (
              <select aria-label="Role" data-testid="nodes-role" className={SEL} value={f.role} onChange={(e) => setF({ role: e.target.value as NodeFilter['role'] })}>
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            )}
            <select aria-label="Sort by" data-testid="nodes-sort" className={SEL} value={f.sort} onChange={(e) => setF({ sort: e.target.value as NodeSort })}>
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  Sort: {s.label}
                </option>
              ))}
            </select>
            {filtered && (
              <Button variant="quiet" className="max-md:min-h-[44px]" data-testid="nodes-reset" onClick={reset}>
                Reset filters
              </Button>
            )}
            <span className="num ml-auto text-xs text-muted" data-testid="nodes-shown">
              {list.length} shown
            </span>
          </div>

          {q.isPending ? (
            <div className="p-4">
              <SkeletonRows rows={5} cols={8} />
            </div>
          ) : q.error && !q.data ? (
            <div className="p-4">
              <ErrorBox testId="nodes-error" text={`GET /api/admin/nodes: ${(q.error as Error).message}`} busy={q.isFetching} onRetry={() => void q.refetch()} />
            </div>
          ) : nodes.length === 0 && !installing.length ? (
            <div className="p-4">
              <Empty
                title="No nodes yet"
                testId="nodes-empty"
                action={
                  <Button variant="primary" onClick={() => setDialog({ kind: 'add' })}>
                    Add the first node
                  </Button>
                }
              >
                Add one: the center installs it over SSH and keeps managing it through the host agent, or you get a command to run on the server. A node can take any role:
                ingress (OBS connects here), egress (platform sessions), hub.
              </Empty>
            </div>
          ) : list.length === 0 && !(installing.length && !filtered) ? (
            <div className="p-4">
              <Empty
                title="No nodes match the filters"
                testId="nodes-none-match"
                action={
                  <Button onClick={reset} data-testid="nodes-reset-empty">
                    Reset filters
                  </Button>
                }
              />
            </div>
          ) : (
            <>
              <NodesTable
                nodes={list}
                installing={filtered ? [] : installing}
                relay={showRelay}
                load={showLoad}
                roles={showRoles}
                prov={prov.on}
                byNode={byNode}
                info={info}
                open={open}
                setOpen={setOpen}
                onChanged={refresh}
                onProgress={(h) => setDialog({ kind: 'job', host: h })}
              />
              <NodeCards nodes={list} installing={filtered ? [] : installing} relay={showRelay} byNode={prov.on ? byNode : undefined} info={info} onProgress={(h) => setDialog({ kind: 'job', host: h })} />
            </>
          )}
          {revoked > 0 && (
            <div className="border-t border-line px-4 py-2.5">
              <Button variant="quiet" className="max-md:min-h-[44px]" aria-expanded={f.revoked} data-testid="nodes-show-revoked" onClick={() => setF({ revoked: !f.revoked })}>
                {f.revoked ? 'Hide' : 'Show'} {revoked} revoked
              </Button>
            </div>
          )}
        </section>

        {nodes.length > 0 && nodes.length <= 3 && showRelay && !filtered && (
          <p className="rounded-lg border border-dashed border-line-2 px-4 py-3 text-sm text-muted" data-testid="nodes-small">
            <b className="text-text-soft">A small network.</b> Streams on an edge node with egress via center reach platforms through a hub, one hop. Relays and multi-hop paths come later.
          </p>
        )}

        {prov.on && pending.length > 0 && (
          <section className="rs-corners rounded-lg border border-line">
            <header className="border-b border-line px-4 py-3">
              <h2 className="text-[15px] font-semibold">Hosts without a node</h2>
            </header>
            <div className="p-4">
              <PendingHosts hosts={pending} info={info} onProgress={(h) => setDialog({ kind: 'job', host: h })} onManage={(h) => setDialog({ kind: 'host', host: h })} />
            </div>
          </section>
        )}
        {dialog?.kind === 'add' && <AddNodeDialog keys={prov.keys.data} keysError={prov.keys.error} loading={prov.keys.isLoading} onClose={() => setDialog(null)} />}
        {dialog?.kind === 'job' && (
          <Modal title={`Install of ${dialog.host.slug}`} testId="install-dialog" onClose={() => setDialog(null)}>
            {dialog.job ? <InstallProgress jobId={dialog.job.id} initial={dialog.job} /> : <LatestJob hostId={dialog.host.id} />}
          </Modal>
        )}
        {dialog?.kind === 'host' && (
          <Modal title={`Host ${dialog.host.slug}`} testId="host-dialog" onClose={() => setDialog(null)}>
            <HostCard hostId={dialog.host.id} info={info} badge onGone={() => setDialog(null)} />
          </Modal>
        )}
      </div>
    </HostsLiveContext.Provider>
  )
}

function StreamsCell({ n }: { n: NodeInfo }) {
  const l = n.load
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="num font-mono text-xs whitespace-nowrap" title={l ? `${l.streams_ingress} ingress · ${l.streams_transit} transit · ${l.streams_egress} egress (relay)` : 'Live streams placed on the node'}>
        {n.streams} live
      </span>
      {l && l.streams_egress > 0 && <span className="num font-mono text-[11px] whitespace-nowrap text-muted">{l.streams_egress} relayed out</span>}
      <span className="num truncate font-mono text-[11px] text-muted" data-testid={`node-outputs-${n.id}`} title={outputsText(n.outputs)}>
        {outputsText(n.outputs)}
      </span>
      {n.status === 'online' && !!n.egress_bps && <span className="num font-mono text-[11px] text-muted">{mbps(n.egress_bps)}</span>}
    </span>
  )
}

/**
 * Node groups first (NG-06 A, GROUP-UI): a header row per node group (name, address,
 * members, publishers) opening its page, its members under it; then the other nodes.
 * The order inside each part is the list's (filters and sort).
 */
type Part = { group?: FailoverGroup; nodes: NodeInfo[] }
function byGroup(nodes: NodeInfo[], groups: FailoverGroup[]): Part[] {
  const ng = groups.filter(isNodeGroup).sort((a, b) => a.name.localeCompare(b.name))
  const of = (n: NodeInfo) => (n.failover?.mode === 'group' ? n.failover.group : undefined)
  const parts: Part[] = ng.map((g) => ({ group: g, nodes: nodes.filter((n) => of(n) === g.id) })).filter((p) => p.nodes.length > 0 || p.group.members.length === 0) // an empty group keeps its row: the way to its page
  const grouped = new Set(parts.flatMap((p) => p.nodes.map((n) => n.id)))
  const rest = nodes.filter((n) => !grouped.has(n.id))
  return rest.length ? [...parts, { nodes: rest }] : parts
}

function GroupHead({ g, nodes }: { g: FailoverGroup; nodes: NodeInfo[] }) {
  const pubs = nodes.reduce((a, n) => a + (n.streams ?? 0), 0)
  return (
    <Link to={groupPath(g.id)} className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 hover:underline" aria-label={t('group.row.open', { name: g.name })} data-testid={`group-link-${g.id}`}>
      <Icon name="chevron-right" size={16} className="text-text-soft" />
      <span className="font-mono font-semibold text-text">{g.name}</span>
      {g.default && <Tag data-testid={`group-default-${g.id}`}>{t('gx.default.tag')}</Tag>}
      {g.fqdn && <span className="truncate font-mono text-[11px] text-muted">{g.fqdn}</span>}
      <span className="num font-mono text-[11px] text-muted">{t('group.row.members', { n: nodes.length })}</span>
      <span className="num font-mono text-[11px] text-muted">{t('group.row.publishers', { n: pubs })}</span>
    </Link>
  )
}

/** Column weights of the table (fixed layout: the table always fits its box). */
type Col = { key: string; label: ReactNode; w: number; sort?: NodeSort; cls?: string }

function NodesTable(p: {
  nodes: NodeInfo[]
  installing: Host[]
  relay: boolean
  load: boolean
  roles: boolean
  prov: boolean
  byNode: Map<string, Host>
  info?: InstallInfo
  open: string | null
  setOpen: (id: string | null) => void
  onChanged: () => Promise<unknown>
  onProgress: (h: Host) => void
}) {
  const [f, setF] = useFilter()
  const fo = useFailover()
  const parts = byGroup(p.nodes, fo.data?.groups ?? [])
  const cols: Col[] = [
    { key: 'node', label: 'Node', w: 16, sort: 'name' },
    { key: 'state', label: 'Status', w: 17, sort: 'state' },
    ...(p.roles ? [{ key: 'roles', label: 'Roles', w: 8 }] : []),
    { key: 'region', label: 'Region', w: 9, sort: 'region' as NodeSort },
    ...(p.load ? [{ key: 'load', label: 'Load', w: 11, sort: 'cpu' as NodeSort }] : []),
    { key: 'streams', label: 'Streams', w: 11, sort: 'streams' },
    { key: 'act', label: <span className="sr-only">Settings</span>, w: 4 },
  ]
  const sum = cols.reduce((a, c) => a + c.w, 0)
  const span = cols.length
  return (
    <div className="hidden md:block">
      <table data-testid="nodes-table" className="w-full table-fixed text-left text-sm">
        <colgroup>
          {cols.map((c) => (
            <col key={c.key} style={{ width: `${((c.w / sum) * 100).toFixed(2)}%` }} />
          ))}
        </colgroup>
        <thead className="border-b border-line text-xs text-muted">
          <tr>
            {cols.map((c) => (
              <th scope="col" key={c.key} className="px-3 py-2 font-medium first:pl-4 last:pr-4" aria-sort={c.sort && f.sort === c.sort ? (c.sort === 'name' || c.sort === 'region' ? 'ascending' : 'descending') : undefined}>
                {c.sort ? (
                  <button type="button" onClick={() => setF({ sort: c.sort })} className={`inline-flex items-center gap-1 hover:text-text ${f.sort === c.sort ? 'text-text' : ''}`}>
                    {c.label}
                    {f.sort === c.sort && <span aria-hidden>▾</span>}
                  </button>
                ) : (
                  c.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {p.installing.map((h) => (
            <tr key={h.id} data-testid={`node-installing-${h.id}`} className="[&>td]:overflow-hidden [&>td]:px-3 [&>td]:py-2 [&>td]:align-top [&>td:first-child]:pl-4">
              <td>
                <p className="truncate font-mono font-semibold">{h.slug}</p>
                <p className="truncate font-mono text-xs text-muted">{h.address}</p>
              </td>
              <td>
                <StateBadge spec={INSTALLING_SPEC} />
              </td>
              <td colSpan={span - 2} className="text-xs text-muted">
                The center is installing it over SSH; it shows up as a node after the join.{' '}
                <button type="button" className="text-text-soft underline underline-offset-2" data-testid={`node-installing-open-${h.id}`} onClick={() => p.onProgress(h)}>
                  Open progress
                </button>
              </td>
            </tr>
          ))}
          {parts.map((part) => [
            part.group && (
              <tr key={`g-${part.group.id}`} data-testid={`group-row-${part.group.id}`} className="bg-bg">
                <td colSpan={span} className="px-4 py-2">
                  <GroupHead g={part.group} nodes={part.nodes} />
                </td>
              </tr>
            ),
            ...part.nodes.map((n) => (
              <Row key={n.id} n={n} cols={cols} inGroup={!!part.group} relay={p.relay} host={p.prov ? (p.byNode.get(n.id) ?? null) : undefined} info={p.info} open={p.open === n.id} onToggle={() => p.setOpen(p.open === n.id ? null : n.id)} onChanged={p.onChanged} />
            )),
          ])}
        </tbody>
      </table>
    </div>
  )
}

function Row({ n, cols, inGroup, relay, host, info, open, onToggle, onChanged }: {
  n: NodeInfo
  cols: Col[]
  /** a member under its group's header row: indented */
  inGroup?: boolean
  /** the center has relay data: the peers icon in Status and the relay key under the gear */
  relay: boolean
  /** undefined = provisioning off (no column); null = a node installed by hand */
  host?: Host | null
  info?: InstallInfo
  open: boolean
  onToggle: () => void
  onChanged: () => Promise<unknown>
}) {
  const revoked = n.status === 'revoked'
  const cell: Record<string, ReactNode> = {
    node: (
      <span className="flex min-w-0 flex-col">
        <Link to={nodePath(n.id)} className="truncate font-mono font-semibold hover:underline" data-testid={`node-link-${n.id}`} title={`Open ${n.name}`}>
          {n.name}
        </Link>
        <span className="truncate font-mono text-[11px] text-muted" title={`${n.id} · ${n.version || 'version unknown'}`}>
          {n.id} · <LogicVersion n={n} host={host} />
        </span>
        {n.endpoints?.srt ? (
          <span data-testid={`node-srt-${n.id}`} className="truncate font-mono text-[11px] text-muted" title="SRT listener the node announces (UDP)">
            SRT {n.endpoints.srt} udp
          </span>
        ) : (
          n.status === 'online' && (
            <span data-testid={`node-srt-${n.id}`} data-off="true" className="text-[11px] text-muted">
              No SRT announced
            </span>
          )
        )}
        {outOfSync(n) && (
          <span data-testid={`node-sync-${n.id}`} className="mt-0.5 text-[11px] leading-snug text-warn" title="The node has not confirmed the latest list of outputs">
            ▲ Outputs not confirmed: sent rev {n.desired_rev ?? 0}, node at {n.acked_rev ?? 0}
          </span>
        )}
      </span>
    ),
    state: <StatusCell n={n} host={host} info={info} relay={relay} />,
    roles: (
      <span className="flex flex-col gap-1">
        <RoleGlyphs roles={n.roles} testId={`node-roles-${n.id}`} />
        <span className="text-[11px] text-muted">{n.egress === 'via_center' ? 'via center' : 'direct'}</span>
      </span>
    ),
    region: (
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-mono text-xs" title={n.region}>
          {n.region || '—'}
        </span>
        <span className="truncate text-[11px] text-muted" title={n.provider}>
          {n.provider || (cols.some((c) => c.key === 'roles') ? '' : n.egress === 'via_center' ? 'via center' : 'direct')}
        </span>
      </span>
    ),
    load: <LoadCell n={n} />,
    streams: <StreamsCell n={n} />,
    act: !revoked && (
      <button
        type="button"
        aria-expanded={open}
        aria-label={open ? `Close settings of ${n.name}` : `Settings of ${n.name}`}
        title={open ? 'Close settings' : 'Node settings'}
        data-testid={`node-manage-${n.id}`}
        onClick={onToggle}
        className={`inline-grid h-8 w-8 place-items-center rounded-md border focus-visible:border-accent focus-visible:shadow-focus focus-visible:outline-none ${open ? 'border-accent/60 text-accent' : 'border-transparent text-muted hover:border-line-2 hover:text-text'}`}
      >
        <GearIcon />
      </button>
    ),
  }
  return (
    <>
      <tr
        data-testid={`node-row-${n.id}`}
        data-status={n.status}
        data-view={nodeView(n)}
        data-drained={!n.accept_ingest ? 'true' : undefined}
        className={`[&>td]:overflow-hidden [&>td]:px-3 [&>td]:py-2 [&>td]:align-top ${inGroup ? '[&>td:first-child]:pl-9' : '[&>td:first-child]:pl-4'} [&>td:last-child]:pr-4 ${revoked ? 'text-muted opacity-70' : ''}`}
      >
        {cols.map((c) => (
          <td key={c.key} className={c.key === 'act' ? 'text-right' : ''}>
            {cell[c.key]}
          </td>
        ))}
      </tr>
      {open && !revoked && (
        <tr>
          <td colSpan={cols.length} className="bg-panel px-4 py-4">
            {relay && n.relay && (
              <div className="mb-6 border-b border-line pb-6">
                <RelayKeyBlock n={n} />
              </div>
            )}
            <p className="mb-4 text-sm text-muted">
              Links, streams and history are on the{' '}
              <Link to={nodePath(n.id)} className="text-text-soft underline underline-offset-2">
                node card
              </Link>
              .
            </p>
            {host && (
              <div className="mb-6 border-b border-line pb-6">
                <HostCard hostId={host.id} info={info} node={n} />
              </div>
            )}
            <NodeManage n={n} onChanged={onChanged} onClose={onToggle} />
          </td>
        </tr>
      )}
    </>
  )
}

/** Phone: one card per node, state first; the card opens the node card. */
function NodeCards({ nodes: list, installing, relay, byNode, info, onProgress }: { nodes: NodeInfo[]; installing: Host[]; relay: boolean; byNode?: Map<string, Host>; info?: InstallInfo; onProgress: (h: Host) => void }) {
  const fo = useFailover()
  const parts = byGroup(list, fo.data?.groups ?? [])
  return (
    <ul className="divide-y divide-line md:hidden" data-testid="nodes-cards">
      {installing.map((h) => (
        <li key={h.id} className="flex flex-col gap-1.5 px-4 py-3" data-testid={`node-card-installing-${h.id}`}>
          <div className="flex items-start justify-between gap-3">
            <span className="min-w-0 truncate font-mono text-[15px] font-semibold">{h.slug}</span>
            <StateBadge spec={INSTALLING_SPEC} />
          </div>
          <button type="button" onClick={() => onProgress(h)} className="min-h-[44px] self-start text-sm text-text-soft underline underline-offset-2">
            Open install progress
          </button>
        </li>
      ))}
      {parts.map((part) => [
        part.group && (
          <li key={`g-${part.group.id}`} className="bg-bg px-4 py-3" data-testid={`group-card-${part.group.id}`}>
            <GroupHead g={part.group} nodes={part.nodes} />
          </li>
        ),
        ...part.nodes.map((n) => {
        const v = nodeView(n)
        const issues = nodeIssues(n)
        const host = byNode?.get(n.id)
        return (
          <li key={n.id} data-testid={`node-card-${n.id}`} data-view={v} className={n.status === 'revoked' ? 'opacity-70' : ''}>
            <Link to={nodePath(n.id)} className="flex min-h-[44px] flex-col gap-1.5 px-4 py-3 active:bg-panel">
              <span className="flex items-start justify-between gap-3">
                <span className="flex min-w-0 flex-col">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate font-mono text-[15px] font-semibold">{n.name}</span>
                    <RoleGlyphs roles={n.roles} />
                  </span>
                  <span className="truncate text-xs text-muted">
                    <span className="font-mono">{n.region || '—'}</span>
                    {n.provider ? ` · ${n.provider}` : ''} · {n.egress === 'via_center' ? 'via center' : 'direct'}
                  </span>
                </span>
                <StateBadge spec={NODE_SPEC[v]} />
              </span>
              {v === 'degraded' && <span className="text-xs text-warn">{issues.join(' · ')}</span>}
              <Lifecycle n={n} />
              {n.status === 'online' && (
                <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                  {n.load && (
                    <span className="num font-mono">
                      CPU {Math.round(n.load.cpu_pct)}% · mem {Math.round(n.load.mem_pct)}%
                    </span>
                  )}
                  <span className="num font-mono text-text-soft">{n.streams} live</span>
                  {n.load && <span className="num font-mono text-muted">↑{bps(n.load.tx_bps)}</span>}
                  {n.clock?.suspect && <span className="text-warn">▲ clock</span>}
                </span>
              )}
              {relay && n.relay && (
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted">
                  {['disabled', 'unsupported', 'revoked'].includes(n.relay.state) ? (
                    <span>─ {RELAY_STATE_SPEC[n.relay.state].word.toLowerCase()}</span>
                  ) : (
                    <span>
                      {n.relay.in_peers ? '✓ in peers' : '○ peers pending'} · v{n.relay.peers_version_acked}/{n.relay.peers_version}
                    </span>
                  )}
                  {n.relay.enabled && n.relay.peers_total !== undefined && n.relay.peers_acked !== undefined && n.relay.peers_total > 0 && (
                    <span className={n.relay.peers_acked < n.relay.peers_total ? 'text-warn' : ''}>
                      trusted {n.relay.peers_acked}/{n.relay.peers_total}
                    </span>
                  )}
                </span>
              )}
              {host && (
                <span className="text-[11px] text-muted">
                  Host agent {host.online ? 'online' : 'offline'}
                  {info?.release_no && host.release_no && host.release_no < info.release_no ? ` · ▲ ${info.release_no - host.release_no} behind` : ''}
                </span>
              )}
            </Link>
          </li>
        )
      }),
      ])}
    </ul>
  )
}
